import React, { useMemo, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import styled from 'styled-components'

import { Button } from '@nexiahome/ui-common/components/primitives/buttons'
import BreadCrumbLink from 'components/BreadCrumbLink'
import Spinner from 'components/Spinner'
import TextInput from 'components/TextInput'
import AutoComplete from 'components/AutoComplete'

import DealerInfo from 'pages/shared/DealerInfo'

import { ButtonContainer, SinglePanePage, PageTitle } from 'styles/shared'
import { color } from 'styles/colors'

import Api from 'services/api'

import { Dealer } from 'types/Dealer'

import useDealers from 'hooks/useDealers'
import { useServerErrorContext } from 'hooks/ServerErrorContext'

const TransferDevice = () => {
  const navigate = useNavigate()
  const { dealerId: fromSelectedDealerId, deviceId } = useParams()

  const { dealers, areDealersLoaded } = useDealers({ page: 1, pageSize: 'max' })
  const { setServerError } = useServerErrorContext()

  const [toDealer, setToDealer] = useState<Dealer | null>(null)
  const [showSpinner, setShowSpinner] = useState<boolean>(false)

  const getName = (dealer) => {
    return dealer && (dealer.name || `Unknown (${dealer.id})`)
  }

  const fromDealerInfo = useMemo(() => areDealersLoaded ? dealers.find(({ id }) => id.toString() === fromSelectedDealerId) : {}, [dealers, areDealersLoaded, fromSelectedDealerId])

  const options = useMemo(() => {
    return areDealersLoaded
      ? dealers.map(dealer => {
        return { ...dealer, name: `${getName(dealer)} - ${dealer.phoneNumber}` }
      }) : []
  }, [dealers, areDealersLoaded])

  if (!areDealersLoaded) {
    return <Spinner variant="page" />
  }

  const onClickTransfer = () => {
    if (!deviceId) return
    if (confirm(`Do you want to transfer this System ${deviceId} from ${getName(fromDealerInfo)} - ${fromDealerInfo?.phoneNumber} to ${toDealer?.name}`)) {
      setShowSpinner(true)

      Api.Device.migrate(deviceId, fromDealerInfo.id, toDealer?.id)
        .then(() => {
          setShowSpinner(false)
          navigate('/devices')
        }).catch(() => {
          setServerError('Failed to transfer device')
          setShowSpinner(false)
        })
    }
  }

  return (
    <SinglePanePage>
      <BreadCrumbLink
        toRoute="/devices"
        direction="back"
        label="Device Search"
      />
      <S.PageTitle>Transfer Device</S.PageTitle>
      <S.Container>
        <S.InnerContainer>
          <S.TextInput
          label="From this dealer"
          disabled
          value={getName(fromDealerInfo) + ' - ' + fromDealerInfo?.phoneNumber}
          />
          <S.DealerContainer>
            <DealerInfo dealer={fromDealerInfo} />
            <S.System>System {deviceId}</S.System>
          </S.DealerContainer>
        </S.InnerContainer>
        {showSpinner
          ? <S.Spinner variant="secondaryButton" />
          : <S.Icon data-testid="right-arrow" className="icon-arrow-right4" />
        }
        <S.InnerContainer>
          <AutoComplete
          options={options.filter(({ id }) => (id !== fromDealerInfo.id))}
          label="To this dealer"
          initialValue={toDealer?.name || ''}
          onSelected={(dealer) => { setToDealer(dealer) }}
          getOptionKey={(option) => option.id}
          getOptionLabel={(option) => option.name}
          />
          <S.DealerContainer>
            <DealerInfo dealer={toDealer} />
          </S.DealerContainer>
        </S.InnerContainer>
      </S.Container>
      <ButtonContainer>
        <Button decoration="secondary" styles={{ button: { marginRight: 15 } }} text="Cancel" onPress={() => navigate('/devices')} />
        <Button decoration="positive" enabled={!!toDealer} text="Transfer" onPress={onClickTransfer} showSpinner={showSpinner} />
      </ButtonContainer>
    </SinglePanePage>
  )
}

const S = {
  Container: styled.div`
    display: flex;
    flex-direction: row;
    justify-content: space-around;
  `,
  PageTitle: styled(PageTitle)`
    margin-top: 10px;
  `,
  InnerContainer: styled.div`
    display: flex;
    width: 45%;
    flex-direction: column;
  `,
  TextInput: styled(TextInput)`
    width: 100%;
  `,
  System: styled.div`
    padding-top: 30px;
    font-weight: 600px;
  `,
  DealerContainer: styled.div`
    padding: 20px;
    min-height: 400px;
    border: 1px solid ${color('border')};
  `,
  Spinner: styled(Spinner)`
    margin: 60px 0 0 0;
    width: 32px;
    height: 32px;
    border-radius: 18px;
  `,
  Icon: styled.i`
    font-size: 32px;
    font-weight: 100;
    margin-top: 60px;
  `
}

export default TransferDevice
